import { useEffect } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CtaButton } from "@/components/Cta";
import { LOCALITIES } from "@/config";
import { ArrowLeft, ShieldCheck, IndianRupee, Building2, PhoneCall } from "lucide-react";

const PRINCIPLES = [
  {
    icon: ShieldCheck,
    title: "A human checks every bay",
    body: "A verification call and a photo match before anything is published, and an in-person site visit for any address with five or more bays. A listing that fails the check never appears.",
  },
  {
    icon: IndianRupee,
    title: "Fixed, honest money",
    body: "Hosts pay one flat annual listing fee and keep their full asking price. We take no percentage of rent and no per-booking cut from the host.",
  },
  {
    icon: Building2,
    title: "A company you can find",
    body: "Sahasamstapaka Private Limited, registered in Jayanagar, Bengaluru, with a GSTIN on every invoice and a named grievance officer you can write to.",
  },
  {
    icon: PhoneCall,
    title: "Phones, not chat bots",
    body: "There is no in-app messaging. Once a host accepts, both sides get each other's number and talk like neighbours. Refunds and disputes are handled by our team, by phone.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "About NightBay — monthly overnight parking in Bengaluru";
  }, []);

  return (
    <div className="App" data-testid="about-page">
      <Header />
      <main>
        <section className="border-b border-line">
          <div className="nb-shell max-w-[820px] pb-14 pt-[120px]">
            <a href="/" className="inline-flex items-center gap-2 text-sm text-ink3 hover:text-brand">
              <ArrowLeft size={15} aria-hidden="true" /> Back to nightbay.in
            </a>
            <p className="nb-eyebrow mt-8">About us</p>
            <h1 data-testid="about-h1" className="mt-5 text-4xl sm:text-5xl lg:text-6xl">
              Bengaluru has the parking. It just isn't where the cars are.
            </h1>
            <p className="mt-6 text-base text-ink2 sm:text-lg">
              Across this city, private bays sit empty every single night — a driveway behind a
              gate, an allotted bay whose owner travels, visitor bays a society never fills, an office
              basement after the last person leaves. A few streets away, someone is circling at 11pm
              looking for somewhere their car can stand until morning.
            </p>
            <p className="mt-5 text-base text-ink2">
              NightBay exists to put those two people in touch, for a month at a time, with the
              awkward parts — is it real, what does it cost, who do I call — handled before either of
              them has to worry about it.
            </p>
          </div>
        </section>

        <section data-testid="about-what" className="nb-section bg-surface2">
          <div className="nb-shell max-w-[820px]">
            <h2 className="nb-h2">What we actually do</h2>
            <p className="mt-6 text-base text-ink2">
              NightBay is a marketplace for <strong className="text-ink">monthly, overnight</strong>{" "}
              parking. A host lists a bay they own or have been allotted. A renter books it for a
              minimum of one month and parks there every night. The bay belongs to the host and the
              money mostly goes to the host; we verify, match, collect payment, issue the invoice and
              pick up the phone when something goes wrong.
            </p>
            <p className="mt-5 text-base text-ink2">
              We are not an hourly car park, not a valet service and not a security company. We hold
              no keys, provide no guard and offer no insurance — a bay is protected by its own
              building's arrangements, exactly as the host describes them. Saying that plainly is part
              of the product.
            </p>
          </div>
        </section>

        <section data-testid="about-principles" className="nb-section">
          <div className="nb-shell max-w-[820px]">
            <h2 className="nb-h2">How we've chosen to run it</h2>
            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {PRINCIPLES.map(({ icon: Icon, title, body }, i) => (
                <div key={title} data-testid={`about-principle-${i}`} className="nb-card">
                  <Icon size={20} className="text-brand" aria-hidden="true" />
                  <h3 className="mt-3 text-base">{title}</h3>
                  <p className="mt-2 text-sm text-ink2">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="about-where" className="nb-section bg-surface2">
          <div className="nb-shell max-w-[820px]">
            <h2 className="nb-h2">Where we are, and where we're going</h2>
            <p className="mt-6 text-base text-ink2">
              We're starting in {LOCALITIES.length} Bengaluru localities — {LOCALITIES.join(", ")} —
              and going deep in each one rather than thin across the city. A locality only works when
              there are both bays and drivers in it, so we open the next one when people in it ask
              us to.
            </p>
            <p className="mt-5 text-base text-ink2">
              We're onboarding the first hosts and renters by hand, on the phone, one at a time. It is
              slower than a launch button and it is the only way we know to be sure that the first
              hundred bays on this platform are real.
            </p>
          </div>
        </section>

        <section data-testid="about-company" className="nb-section">
          <div className="nb-shell max-w-[820px]">
            <h2 className="nb-h2">The company</h2>
            <dl className="mt-8 overflow-hidden rounded-nb2 border border-line">
              {[
                ["Operator", "Sahasamstapaka Private Limited"],
                ["GSTIN", "29ABUCS6856L1Z3"],
                [
                  "Registered office",
                  "No. 11 (old No. 76), Diagonal Road, 3rd Block, Jayanagar, Ward No. 167 (old No. 59), Bengaluru, Karnataka 560011, India",
                ],
                ["Support", "support@nightbay.in · +91 97411 46164"],
                ["Grievance Officer", "Prathibha K P · Prathibha.kp@nightbay.in"],
                ["Data Protection Officer", "Abinaya Balas · abinaya@bsrlawfirm.in"],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="grid gap-1 border-b border-line bg-surface2 px-4 py-4 last:border-b-0 sm:grid-cols-[200px_1fr] sm:gap-4"
                >
                  <dt className="font-head text-xs tracking-[0.12em] text-ink3">{k}</dt>
                  <dd className="text-sm text-ink2">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 text-sm text-ink3">
              Our{" "}
              <a href="/terms" className="text-brand underline">
                Terms &amp; Conditions
              </a>{" "}
              and{" "}
              <a href="/privacy" className="text-brand underline">
                Privacy Policy
              </a>{" "}
              are published in full, and the app and this site read them from the same source.
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <CtaButton side="renter" testId="about-cta-renter">
                Find a spot near me
              </CtaButton>
              <CtaButton side="host" variant="ghost" testId="about-cta-host">
                I have a bay to rent out
              </CtaButton>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
